import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ImageCroppedEvent, LoadedImage, base64ToFile } from 'ngx-image-cropper';
import { BehaviorSubject, first, forkJoin, Observable, Subject, takeUntil } from 'rxjs';
import { LgvUserService, NotificationService } from '../../core/core.module';
import { LgvCompanyService } from '../../core/services/lgv-company.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'lgv-image-uploader-modal',
    templateUrl: './image-uploader-modal.component.html',
    styleUrls: ['./image-uploader-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageUploaderModalComponent implements OnInit, OnDestroy, AfterViewInit {

    imageChangedEvent: any = '';
    croppedImage: any = '';

    isLoading$ = new BehaviorSubject(false);

    base64String: any;
    errorFetchingImageForEditing = false;

    route: any;

    _pageMode = ImagePageMode.Default;
    pageMode$ = new BehaviorSubject(this._pageMode);

    hdImage: HDImage;
    onDestroy$ = new Subject();

    get isUserImage(): boolean { return this.hdImage.key.startsWith("User_"); }
    get isCompanyImage(): boolean { return this.hdImage.key.startsWith("Company_"); }

    constructor(
        public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
        private notificationSvc: NotificationService,
        private lgvUserSvc: LgvUserService,
        private lgvCompanySvc: LgvCompanyService,
        @Inject(MAT_DIALOG_DATA) public data: any) {
    }


    ngOnInit(): void {
        this.hdImage = this.data?.hdImage;
        console.log(this.hdImage)

        //    console.log('hdImage:');
        //    console.log(this.hdImage);
    }

    ngAfterViewInit() {
    }

    ngOnDestroy(): void {
        this.onDestroy$.complete();
    }

    onEditCurrentImage() {
        this.errorFetchingImageForEditing = false;
        this.getBase64FromUrl();
    }

    getBase64FromUrl() {
        this.isLoading$.next(true);
        this.pageMode$.next(ImagePageMode.Edit);

        this.hdImage.url$.pipe(first(), takeUntil(this.onDestroy$)).subscribe(url => {

            //console.log('url$');
            //console.log(url);

            if (url && url.length > 0) {
                // Get the remote image as a Blob with the fetch API
                fetch(url)
                    .then((res) => res.blob())
                    .then((blob) => {
                        // Read the Blob as DataURL using the FileReader API
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            //console.log('Raw Result:');
                            //console.log(reader.result);
                            this.base64String = reader.result;
                            this.isLoading$.next(false);
                        };
                        reader.readAsDataURL(blob);
                    }).catch((error) => {
                        this.isLoading$.next(false);
                        this.pageMode$.next(ImagePageMode.Default);
                        this.errorFetchingImageForEditing = true;
                        //this.notificationSvc.error('Image Could not be loaded');
                    });
            } else {
                this.isLoading$.next(false);
            }

        });

    }

    fileChangeEvent(event: any): void {
        this.imageChangedEvent = event;
    }

    imageCropped(event: ImageCroppedEvent) {
        this.croppedImage = event.base64;
    }

    imageLoaded(image: LoadedImage) {
        // show cropper
    }

    cropperReady() {
        // cropper ready
    }

    loadImageFailed() {
        // show message
    }

    onUpload() {
        this.errorFetchingImageForEditing = false;
        this.pageMode$.next(ImagePageMode.Upload);
    }

    onPreview(): void {
        this.pageMode$.next(ImagePageMode.Preview);
    }
    onCancel(): void {
        this.dialogRef.close({ action: false });
    }

    onDelete() {

        if (this.isUserImage) {
            this.lgvUserSvc.deleteAssetFile(this.data?.userId, this.hdImage.key).pipe(first(), takeUntil(this.onDestroy$)).subscribe(x => {
                if (this.hdImage.key === 'User_Profile') {
                    this.lgvUserSvc.loadUserAssets(this.data?.userId);
                }
                this.dialogRef.close({ action: true });
            },
                (err: Error) => {
                    const msg = 'There was an error deleting the image';
                    console.log(err);
                    this.notificationSvc.error(msg);
                }
            );
        }
        else if (this.isCompanyImage) {
            this.lgvCompanySvc.deleteAssetFile(this.data?.companyId, this.hdImage.key).pipe(first(), takeUntil(this.onDestroy$)).subscribe(x => {
                //console.log('response from delete asset:');
                //console.log(x);
                this.dialogRef.close({ action: true });
            },
                (err: Error) => {
                    const msg = 'There was an error deleting the image';
                    console.log(err);
                    this.notificationSvc.error(msg);
                }
            );
        }


    }

    onSave(): void {
        const file = base64ToFile(this.croppedImage);
    
        this.isLoading$.next(true)
        let formData = new FormData();
        formData.append('file', file);
        formData.append('assetType', this.hdImage.key);
    
        let requests = [];
    
        if (this.isUserImage) {
            requests.push(
                this.lgvUserSvc.postAssetFile(this.data?.userId, formData).pipe(first(), takeUntil(this.onDestroy$))
            );
        }
    
        if (this.isCompanyImage) {
            requests.push(
                this.lgvCompanySvc.postAssetFile(this.data?.companyId, formData).pipe(first(), takeUntil(this.onDestroy$))
            );
        }
    
        if (requests.length > 0) {
            forkJoin(requests).subscribe(
                (responses) => {
                    if (this.isUserImage && this.hdImage.key === 'User_Profile') {
                        this.lgvUserSvc.loadUserAssets(this.data?.userId);
                    }
                    this.isLoading$.next(false)
                    this.dialogRef.close({ action: true });
                },
                (err: Error) => {
                    console.error(err);
                    this.isLoading$.next(false)
                    this.notificationSvc.error('There was an error saving the image');
                }
            );
        } else {
            this.dialogRef.close({ action: true });
        }
    }

}

export enum ImagePageMode {
    Default = 'Default',
    Upload = 'Upload',
    Edit = 'Edit',
    Preview = 'Preview'
}

export class HDImage {
    key: string;
    url$?: Observable<string>;
    targetHeight: number;
    maxHeight: number;
    title: string;
    aspectRatio?: number;
    darkBkg: boolean;
    description: string;
}
